import { message } from 'antd';
import { orderBy, query } from 'firebase/firestore';

import { functionAuthRequest } from 'services/API';
import { type IDynamicProperty } from 'stores/DynamicProperties/IDynamicProperty';
import { pluralEnglish } from 'utils/String';

import type Ad from './Ad';
import type Creative from './Creative';
import DocumentCollection from './DocumentCollection';
import VideoBundle, { type VideoBundleSchema } from './VideoBundle';

const videoPropertyNames = [
    'bitrate',
    'maxBitrate',
    'minBitrate',
    'bufferSize',
];

function ifFinitePositiveFloat(value: number): number {
    return Number.isFinite(value) && value > 0 ? value : NaN;
}

function getPropertyValuePositiveFloat(
    property: IDynamicProperty<any> | undefined
): number {
    if (property) {
        if (typeof property.value === 'number') {
            return ifFinitePositiveFloat(property.value);
        }

        if (typeof property.value === 'string') {
            const tval = property.value.trim();
            if (tval === '') return NaN;
            return ifFinitePositiveFloat(+tval);
        }
    }

    return NaN;
}

function* validateVideoProperties(ads: Array<Ad>): Generator<[Ad, string[]]> {
    for (const ad of ads) {
        const invalidProperties = videoPropertyNames.filter((propertyName) => {
            const value = getPropertyValuePositiveFloat(
                ad.properties.getChild(propertyName)
            );
            return !Number.isFinite(value);
        });

        if (invalidProperties.length > 0) {
            yield [ad, invalidProperties];
        }
    }
}

function reportInvalidProperties(
    failures: ReadonlyArray<[Ad, ReadonlyArray<string>]>
) {
    const ads = pluralEnglish(failures.length, 'ad');
    const consoleTable = failures.map((f) => {
        const [ad, badProps] = f;
        return {
            label: ad.label || '<no label>',
            adId: ad.id,
            invalidProperties: badProps.join(','),
        };
    });
    const prelude = `Cannot create video bundle. ${failures.length} ${ads} failed validation.`;
    console.error(prelude);
    console.table(consoleTable, ['label', 'adId', 'invalidProperties']);
    message.error(`${prelude} See the console for more details.`);
}

export default class VideoBundlesManager extends DocumentCollection<
    VideoBundle,
    VideoBundleSchema
> {
    constructor(parent: Creative) {
        super(parent, VideoBundle);
        this.becomeObservable();
    }

    query = () => query(this.collectionRef, orderBy('createTime'));

    new = async (bundleName: string, selectedAds: Array<Ad>) => {
        const failures = Array.from(validateVideoProperties(selectedAds));
        if (failures.length > 0) {
            reportInvalidProperties(failures);
            return;
        }

        const adIds: Array<string> = selectedAds.map((ad) => ad.id);

        await functionAuthRequest(
            'createVideoBundleTask',
            {
                name: bundleName,
                selectedAdIDs: adIds,
            },
            this
        );
    };

    delete = async (jobId: string) => {
        const bundle = this.getById(jobId);
        if (!bundle) return;
        await bundle.delete();
    };
}
